import React, { useEffect, useRef, useState } from "react";
import { FaBolt, FaBoxOpen, FaCheck, FaClock } from "react-icons/fa";
import { formatCurrency } from "../../utils/formatCurrency";
import { formatDeliveryDays, getWeightPlanOptions } from "../../api/deliveryPlanApi";

const planVisual = (name = "") => {
  const n = String(name).toLowerCase();
  if (n.includes("express") || n.includes("priority") || n.includes("fast")) {
    return {
      Icon: FaBolt,
      badge: "Fastest",
      iconWrap: "bg-orange-100 text-orange-600",
    };
  }
  if (n.includes("econom")) {
    return {
      Icon: FaClock,
      badge: "Best value",
      iconWrap: "bg-stone-200 text-stone-700",
    };
  }
  return {
    Icon: FaBoxOpen,
    badge: "Recommended",
    iconWrap: "bg-amber-100 text-amber-700",
  };
};

const DeliveryPlanCards = ({
  options = [],
  selectedId,
  onSelect,
  loading = false,
  currencySymbol = "GBP",
  variant = "compact",
  routeName = "",
  kg = 0,
}) => {
  const hero = variant === "hero";

  if (loading) {
    return hero ? (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[0, 1, 2].map((key) => (
          <div
            key={key}
            className="h-56 rounded-2xl border border-stone-200 bg-stone-50 animate-pulse"
          />
        ))}
      </div>
    ) : (
      <p className="text-sm text-stone-500 mt-2">Loading delivery plans…</p>
    );
  }

  if (!options.length) {
    return hero ? (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-6 text-amber-900">
        <p className="font-semibold">No delivery plans for this parcel</p>
        <p className="text-sm mt-1">
          Packages are loaded from Weight setup for{" "}
          <span className="font-semibold">
            {routeName || "this route"}
          </span>
          {kg > 0 ? ` at ${kg} kg` : ""}. Economy / Standard only appear when
          that same route has a weight band with a Plan assigned — the opposite
          direction is a different route.
        </p>
        <p className="text-sm mt-2">
          In Set-up → Weight, edit the matching {routeName || "route"} row and
          tick Economy and Standard, then save.
        </p>
      </div>
    ) : null;
  }

  return (
    <div className={hero ? "w-full" : "w-full mt-3"}>
      {hero ? null : (
        <p className="text-sm font-semibold text-stone-700 mb-2">
          Delivery plan{options.length > 1 ? "s" : ""}
        </p>
      )}
      <div
        className={
          hero
            ? "grid grid-cols-1 sm:grid-cols-3 gap-4"
            : "grid grid-cols-1 sm:grid-cols-3 gap-2"
        }
      >
        {options.map((plan) => {
          const selected = String(selectedId) === String(plan.deliveryPlanId);
          const { Icon, badge, iconWrap } = planVisual(plan.name);
          if (!hero) {
            return (
              <button
                key={plan.deliveryPlanId}
                type="button"
                onClick={() => onSelect(plan)}
                className={`text-left rounded-xl border-2 px-3 py-3 transition ${
                  selected
                    ? "border-orange-500 bg-orange-50 shadow-sm"
                    : "border-stone-200 bg-white hover:border-orange-300"
                }`}
              >
                <p className="font-semibold text-stone-900">{plan.name}</p>
                <p className="text-xs text-stone-500 mt-0.5">
                  {formatDeliveryDays(plan)}
                </p>
                <p className="text-lg font-bold text-stone-900 mt-2 tabular-nums">
                  {formatCurrency(plan.charge, currencySymbol)}
                </p>
                {plan.unitCost ? (
                  <p className="text-[11px] text-stone-500">
                    {formatCurrency(plan.unitCost, currencySymbol)}/kg
                  </p>
                ) : null}
              </button>
            );
          }
          return (
            <button
              key={plan.deliveryPlanId}
              type="button"
              onClick={() => onSelect(plan)}
              className={`relative text-left rounded-2xl border-2 px-5 py-5 flex flex-col min-h-[230px] transition-all duration-200 ${
                selected
                  ? "border-orange-500 bg-gradient-to-b from-orange-50 to-white shadow-lg ring-2 ring-orange-200 -translate-y-0.5"
                  : "border-stone-200 bg-white hover:border-orange-300 hover:shadow-md"
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <span
                  className={`h-11 w-11 rounded-full flex items-center justify-center text-lg ${iconWrap}`}
                >
                  <Icon />
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 bg-stone-100 text-stone-600">
                  {badge}
                </span>
              </div>
              <p className="text-xl font-bold text-stone-900 mt-4">
                {plan.name}
              </p>
              <p className="text-sm text-stone-500 mt-1">
                {formatDeliveryDays(plan) || "Delivery time set at booking"}
              </p>
              <p className="text-3xl font-extrabold text-stone-900 mt-4 tabular-nums">
                {formatCurrency(plan.charge, currencySymbol)}
              </p>
              {plan.unitCost ? (
                <p className="text-xs text-stone-500 mt-1">
                  {formatCurrency(plan.unitCost, currencySymbol)} per kg
                </p>
              ) : null}
              <div className="mt-auto pt-4">
                {selected ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-700">
                    <FaCheck /> Selected
                  </span>
                ) : (
                  <span className="text-sm font-semibold text-stone-500">
                    Tap to choose
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {options.length > 1 && !selectedId ? (
        <p className={`text-amber-700 ${hero ? "mt-4 text-sm" : "text-xs mt-2"}`}>
          Select a delivery plan to continue.
        </p>
      ) : null}
    </div>
  );
};

export function DeliveryPlanPicker({
  routeId,
  routeName = "",
  kg,
  itemCategoryId,
  dimensionId,
  measurementUnitId,
  volume,
  selectedId,
  preferredPlanId,
  onSelect,
  onOptionsChange,
  currencySymbol = "GBP",
  variant = "compact",
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const onSelectRef = useRef(onSelect);
  const onOptionsChangeRef = useRef(onOptionsChange);
  onSelectRef.current = onSelect;
  onOptionsChangeRef.current = onOptionsChange;

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!routeId || !(Number(kg) > 0)) {
        setOptions([]);
        onOptionsChangeRef.current?.([]);
        return;
      }
      setLoading(true);
      try {
        const rows = await getWeightPlanOptions({
          routeId,
          kg,
          itemCategoryId,
          dimensionId,
          measurementUnitId,
          volume,
        });
        if (cancelled) return;
        setOptions(rows);
        onOptionsChangeRef.current?.(rows);

        const currentId = Number(selectedId) || 0;
        const stillValid = rows.some((r) => r.deliveryPlanId === currentId);
        if (stillValid) return;

        const preferred = Number(preferredPlanId) || 0;
        const preferredRow = rows.find((r) => r.deliveryPlanId === preferred);
        if (preferredRow) {
          onSelectRef.current?.(preferredRow);
          return;
        }
        if (rows.length === 1) {
          onSelectRef.current?.(rows[0]);
          return;
        }
        if (currentId) {
          onSelectRef.current?.(null);
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    routeId,
    kg,
    itemCategoryId,
    dimensionId,
    measurementUnitId,
    volume,
    selectedId,
    preferredPlanId,
  ]);

  return (
    <DeliveryPlanCards
      options={options}
      selectedId={selectedId}
      loading={loading}
      currencySymbol={currencySymbol}
      onSelect={onSelect}
      variant={variant}
      routeName={routeName}
      kg={kg}
    />
  );
}

export default DeliveryPlanCards;
